import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../../components/organisms/Layout";
import Location from "../../../api/Location";

const Stripe = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stripeData, setStripeData] = useState({
    locations: [],
  });

  useEffect(() => {
    document.title = "HeyHire Portal - Stripe Management";
    loadStripeData();
  }, []);

  const loadStripeData = async () => {
    setLoading(true);
    try {
      // Load locations data only
      const locationsRes = await Location.getAll();

      // Process locations data safely
      let processedLocations = [];
      if (locationsRes?.data) {
        const locationData = locationsRes.data;
        if (Array.isArray(locationData?.data)) {
          processedLocations = locationData.data;
        } else if (Array.isArray(locationData)) {
          processedLocations = locationData;
        } else if (locationData && typeof locationData === "object") {
          // If it's a single object, wrap it in an array
          processedLocations = [locationData];
        }
      }

      setStripeData({
        locations: processedLocations,
      });
    } catch (error) {
      console.error("Error loading Stripe data:", error);
      toast.error(t("common.something-went-wrong"));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenStripeDetails = (location) => {
    navigate(`/admin/stripe/location/${location.id}`);
  };

  if (loading) {
    return (
      <DashboardLayout >
        <div className="text-center">
          <i className="fad fa-circle-notch fa-spin text-4xl text-primary" />
          <p className="mt-4 text-gray-600">
            Loading locations...
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Stripe Management
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Manage locations for Stripe payment terminals and readers
              </p>
            </div>
            <button
              onClick={loadStripeData}
              className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 transition-colors"
            >
              <i className="far fa-refresh mr-2" />
              Refresh Data
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="mb-8">
          <div className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Business Locations
                </p>
                <p className="text-3xl font-bold text-gray-900">
                  {Array.isArray(stripeData.locations)
                    ? stripeData.locations.length
                    : 0}
                </p>
                <p className="text-xs text-gray-500">
                  Active locations for Stripe terminals
                </p>
              </div>
              <div className="p-4 rounded-full bg-orange-100">
                <i className="far fa-map-marker-alt text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Locations Content */}
        <div className="bg-white">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Business Locations for Stripe Terminals
            </h3>
            <p className="text-sm text-gray-600">
              Manage locations where Stripe payment terminals and readers can be
              deployed for in-person payments.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.isArray(stripeData.locations) &&
                stripeData.locations
                  .filter(
                    (location) => location && typeof location === "object"
                  )
                  .map((location, index) => {
                    // Extract data from nested objects correctly
                    const safeName = location?.name || "Unknown Location";
                    const safeAddress =
                      location?.address?.address || "No address";
                    const safeCity = location?.address?.city || "N/A";
                    const safeState = location?.address?.state || "N/A";
                    const safeZip = location?.address?.zip || "";

                    return (
                      <div
                        key={location?.id || index}
                        className="border border-gray-200"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-semibold text-gray-900">
                            {safeName}
                          </h4>
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            Active
                          </span>
                        </div>
                        <div className="space-y-2 text-sm text-gray-600">
                          <div className="flex items-center">
                            <i className="far fa-map-marker-alt mr-2" />
                            {safeAddress}
                          </div>
                          <div className="flex items-center">
                            <i className="far fa-city mr-2" />
                            {safeCity}, {safeState} {safeZip}
                          </div>
                          {location?.company?.name && (
                            <div className="flex items-center">
                              <i className="far fa-building mr-2" />
                              {location.company.name}
                            </div>
                          )}
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-200">
                          <button
                            onClick={() => handleOpenStripeDetails(location)}
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium w-full text-center hover:bg-primary-50"
                          >
                            View Stripe Details
                          </button>
                        </div>
                      </div>
                    );
                  })}
            </div>

            {(!Array.isArray(stripeData.locations) ||
              stripeData.locations.length === 0) && (
              <div className="text-center py-12">
                <i className="far fa-map-marker-alt text-4xl text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">
                  No locations found
                </h3>
                <p className="text-gray-600">
                  Add business locations to manage Stripe payment terminals and
                  readers.
                </p>
                <button className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors">
                  Add Location
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Stripe;
